import { LogLevel, LogService } from "@/services/logging";
import NotificationService from "@/services/notification.service";
import { handleErrorAndPrepareMessage } from "@/utils/helpers/common";

/**
 * Error classification, redaction and journaling for order actions.
 *
 * The orders package re-wraps its own coded errors at several layers (order
 * model → line insertion → line categorization), each prepending a generic
 * "Failed to create/insert order lines:" prefix and keeping the wrapped error
 * in `details.originalError`. Everything here works on that wrap chain.
 */

/** The error plus every error it wraps via `details.originalError`. */
export function errorChain(error: unknown): unknown[] {
  const chain: unknown[] = [];
  let current: unknown = error;
  for (let depth = 0; depth < 10 && current; depth++) {
    chain.push(current);
    current = (current as { details?: { originalError?: unknown } })?.details
      ?.originalError;
  }
  return chain;
}

/**
 * Deepest error of the wrap chain — it carries the meaningful message and,
 * for line-creation failures, structured `details.failures`.
 */
export function unwrapOrderError(error: unknown): unknown {
  const chain = errorChain(error);
  return chain[chain.length - 1] ?? error;
}

/** String `code` carried by the orders-package business errors, if any. */
export function orderErrorCode(error: unknown): string | undefined {
  const code =
    error && typeof error === "object" && "code" in error
      ? (error as { code?: unknown }).code
      : undefined;
  return typeof code === "string" ? code : undefined;
}

/**
 * Truncated view of a credential-bearing value (promo/voucher code, ticket
 * token, hold token) safe to journal: at most `keep` characters and never
 * more than half the value, so short codes are not fully revealed.
 */
export function secretPrefix(value: string, keep = 8): string {
  const visible = Math.min(keep, Math.floor(value.length / 2));
  return `${value.slice(0, visible)}…`;
}

/**
 * Error types the discount/voucher flow throws for apply attempts the cashier
 * sees rejected in the normal course of selling — not defects. Their messages
 * and `code` fields carry the code value, so log labels use the name and
 * their payloads are redacted before journaling.
 */
const BUSINESS_DECLINE_ERROR_NAMES = [
  "PromoCodeNotApplicableError",
  "VoucherFullyConsumedError",
  "OrderAlreadyCoveredError",
];

/** Declines the packages throw as plain Errors, matched on their message. */
const BUSINESS_DECLINE_MESSAGE_PATTERNS: { pattern: RegExp; label: string }[] =
  [
    {
      pattern: /^External voucher .* is not valid/,
      label: "EXTERNAL_VOUCHER_INVALID",
    },
    {
      pattern: /is already applied to this session/,
      label: "CODE_ALREADY_APPLIED",
    },
  ];

/**
 * check-promocode 4xx statuses that are integration failures rather than
 * code declines: auth (401/403/407), timeout (408) and rate limiting (429).
 * A broken API key must alert as ERROR, not drown as a per-code WARNING.
 */
const CHECK_PROMO_CODE_INTEGRATION_STATUSES = new Set([
  401, 403, 407, 408, 429,
]);

/**
 * Stable label when the failure is a business decline (a code/voucher the
 * order legitimately rejects), undefined when it is a real error. Declines
 * are detected by error name across the wrap chain, by a decline-class 4xx
 * from the check-promocode endpoint, and by message pattern for the declines
 * the packages throw as plain Errors.
 */
function declineLabelFromChain(chain: unknown[]): string | undefined {
  for (const link of chain) {
    const name = (link as { name?: unknown }).name;
    if (
      typeof name === "string" &&
      BUSINESS_DECLINE_ERROR_NAMES.includes(name)
    ) {
      return name;
    }
    if (name === "CheckPromoCodeApiError") {
      const status = (link as { statusCode?: unknown }).statusCode;
      if (
        typeof status === "number" &&
        status >= 400 &&
        status < 500 &&
        !CHECK_PROMO_CODE_INTEGRATION_STATUSES.has(status)
      ) {
        return "CHECK_PROMO_CODE_REJECTED";
      }
    }
    const message = (link as { message?: unknown }).message;
    if (typeof message === "string") {
      const match = BUSINESS_DECLINE_MESSAGE_PATTERNS.find(({ pattern }) =>
        pattern.test(message)
      );
      if (match) {
        return match.label;
      }
    }
  }
  return undefined;
}

/** Masks every double-quoted value inside a message. */
function maskQuotedValues(message: string): string {
  return message.replace(
    /"([^"]+)"/g,
    (_, value: string) => `"${secretPrefix(value, 4)}"`
  );
}

/**
 * Copy of a decline error chain safe to journal: quoted values in every
 * message are masked and the `code` field of decline-named errors (which
 * holds the promo/voucher code itself) is truncated. Own enumerable props
 * plus name/message are preserved, mirroring what the log serializer ships
 * for real Error instances.
 */
function sanitizeDeclineError(link: unknown, depth = 0): unknown {
  if (depth >= 10 || !link || typeof link !== "object") {
    return link;
  }
  const source = link as Record<string, unknown> & {
    name?: unknown;
    message?: unknown;
    details?: { originalError?: unknown };
  };
  const copy: Record<string, unknown> = { ...source };
  if (typeof source.name === "string") {
    copy.name = source.name;
  }
  if (typeof source.message === "string") {
    copy.message = maskQuotedValues(source.message);
  }
  if (
    typeof source.name === "string" &&
    BUSINESS_DECLINE_ERROR_NAMES.includes(source.name) &&
    typeof copy.code === "string"
  ) {
    copy.code = secretPrefix(copy.code, 4);
  }
  if (source.details && typeof source.details === "object") {
    copy.details = { ...(source.details as Record<string, unknown>) };
    if (source.details.originalError !== undefined) {
      (copy.details as Record<string, unknown>).originalError =
        sanitizeDeclineError(source.details.originalError, depth + 1);
    }
  }
  return copy;
}

/**
 * Returns a journal-safe copy of the value when its wrap chain contains a
 * business decline (whose messages and `code` fields carry the code value),
 * and the value untouched otherwise. Works for errors as well as for
 * error-shaped meta objects that embed the chain under `details`.
 */
export function redactDeclineError(value: unknown): unknown {
  return declineLabelFromChain(errorChain(value)) !== undefined
    ? sanitizeDeclineError(value)
    : value;
}

/**
 * True when the value's wrap chain contains a business decline — a
 * promo/voucher code the order legitimately rejects in the normal course of
 * selling, as opposed to a defect.
 */
export function isBusinessDeclineError(value: unknown): boolean {
  return declineLabelFromChain(errorChain(value)) !== undefined;
}

/**
 * Stable root-cause label of an order-action failure, for log messages: the
 * business decline label when the wrap chain contains one, otherwise the
 * `code` of the deepest error (e.g. a SESSION_EXPIRED wrapped inside
 * ORDER_LINE_INSERTION_FAILED reads as SESSION_EXPIRED), otherwise the outer
 * error's own code. Undefined when the failure carries no code at all.
 */
export function orderErrorCauseLabel(error: unknown): string | undefined {
  const chain = errorChain(error);
  const root = chain[chain.length - 1] ?? error;
  return (
    declineLabelFromChain(chain) ??
    orderErrorCode(root) ??
    orderErrorCode(error)
  );
}

/**
 * Human-readable message for an orders-package business error, without the
 * package's internal wrapping noise. Prefers the structured per-line
 * `details.failures[].error` of the innermost error over its message, and
 * cuts boilerplate so e.g. an auto-addon availability failure reads
 * "Addon failures: <addon>: Availability check failed: ..." instead of three
 * nested "Failed to ... order lines:" prefixes around it.
 */
export function extractOrderErrorMessage(error: unknown): string | undefined {
  const deepest = unwrapOrderError(error);
  if (!orderErrorCode(deepest)) {
    return undefined;
  }

  const failures = (
    deepest as { details?: { failures?: { error?: string }[] } }
  ).details?.failures;
  const failureMessages = (failures ?? [])
    .map((failure) => failure.error)
    .filter((msg): msg is string => Boolean(msg));

  const rawMessage = failureMessages.length
    ? failureMessages.join("; ")
    : (deepest as { message?: unknown }).message;
  if (typeof rawMessage !== "string" || !rawMessage) {
    return undefined;
  }

  // Strip any leftover generic wrapper prefixes, then surface the addon
  // failure list directly when present.
  let message = rawMessage.replace(
    /^(Failed to (?:create|insert|update) order lines: )+/,
    ""
  );
  const addonFailuresIndex = message.indexOf("Addon failures:");
  if (addonFailuresIndex > 0) {
    message = message.slice(addonFailuresIndex);
  }
  return message;
}

/**
 * Surfaces an order-action failure to the cashier as an error notification.
 *
 * The orders package throws business errors carrying a string `code` and a
 * human-readable message (e.g. an addon quantity above availability) — show
 * that message, unwrapped from the package's internal layering. Anything
 * else (network, server, unknown) goes through the generic error mapper.
 * SESSION_EXPIRED is skipped: the expiry watcher runs its own close-tab +
 * popup flow and a second toast would be noise.
 *
 * Call from the catch block of every order mutation helper whose UI callers
 * do not show their own notification, then rethrow so callers can still
 * stop their flow.
 */
export function notifyOrderActionError(error: unknown): void {
  if (orderErrorCode(unwrapOrderError(error)) === "SESSION_EXPIRED") {
    return;
  }

  const businessMessage = extractOrderErrorMessage(error);
  if (businessMessage) {
    NotificationService.showCustomMessage(businessMessage, "error");
    return;
  }

  const { errorMessage } = handleErrorAndPrepareMessage(error);
  NotificationService.showCustomMessage(errorMessage, "error");
}

/**
 * Journals a failed order action, classified for alerting. The root cause of
 * the package's wrap chain is appended to the message so the log backend can
 * query and group per cause — a SESSION_EXPIRED wrapped inside
 * ORDER_LINE_INSERTION_FAILED reads as SESSION_EXPIRED, not as the wrapper.
 *
 * Expected business outcomes ship as WARNING with a "Warning" message prefix
 * (so "Error …"-keyed alerts stay quiet for them): session expiry (a
 * lifecycle outcome) and code/voucher declines. Decline errors are redacted
 * before journaling — their messages and `code` fields carry the code value,
 * a still-spendable credential. Every other failure ships as ERROR with the
 * original error intact. Errors are NOT swallowed — callers still receive
 * the throw.
 */
export function logOrderActionError(
  action: string,
  context: Record<string, unknown>,
  error: unknown
): void {
  const chain = errorChain(error);
  const root = chain[chain.length - 1] ?? error;
  const errorCode = orderErrorCode(error);
  const rootCode = orderErrorCode(root);
  const isSessionExpired =
    errorCode === "SESSION_EXPIRED" || rootCode === "SESSION_EXPIRED";
  const declineLabel = declineLabelFromChain(chain);
  const causeLabel = declineLabel ?? rootCode ?? errorCode;
  const expected = isSessionExpired || declineLabel !== undefined;
  LogService.info(
    `${expected ? "Warning" : "Error"} ${action}${
      causeLabel ? `: ${causeLabel}` : ""
    }`,
    {
      ...context,
      error: declineLabel ? sanitizeDeclineError(error) : error,
    },
    expected ? LogLevel.Warning : LogLevel.Error
  );
}
